import type { ChangementTraduction } from '@rosetta/shared';
import type { ReactNode } from 'react';
import { MarqueBulle } from './marque-bulle';
import { PuceCode } from './puce-code';

/**
 * The look of a change the translation declares (#28), in the text and in the key: the
 * `bleu-ciel-tint` background alone, with no underline. Neither the red of "Bloquant", nor the
 * yellow of "À relire", nor the lavender and the bottom line of the invariants. The tint is not
 * the only information (WCAG 1.4.1): the passage has a name ("Changement : ..."), a bubble that says "changement"
 * and a key in the legend.
 */
export const STYLE_CHANGEMENT =
  'rounded-sm bg-bleu-ciel-tint px-1 text-inherit data-[evidence=true]:outline-3 data-[evidence=true]:outline-bleu-nuit data-[evidence=true]:outline-offset-2';

interface MarqueChangementProps {
  changement: ChangementTraduction;
  children: ReactNode;
}

/**
 * A turn of phrase the translation says it changed (`result_json.changements[]` of its
 * sub-task), marked in the translated text of "Avant / après" and explained by a bubble with the
 * type (the code of Tahiti with spaces) and the description, as Tahiti writes them. Same bubble
 * as the additions (`MarqueBulle`). It has no status: it counts in no counter.
 */
export function MarqueChangement({ changement, children }: MarqueChangementProps) {
  return (
    <MarqueBulle
      nom={`Changement : ${changement.type.libelle}`}
      className={STYLE_CHANGEMENT}
      attribut="data-changement"
      bulle={
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <PuceCode>{changement.type.libelle}</PuceCode>
            <span className="font-semibold text-caption text-gray-700">changement</span>
          </div>
          {changement.description === undefined ? null : (
            <p className="mt-2 text-gray-700">{changement.description}</p>
          )}
        </>
      }
    >
      {children}
    </MarqueBulle>
  );
}
